import { CUSTOMER_LOGOS, TESTIMONIALS } from "../content/site";
import { Card, CtaBand, Section, SectionHead } from "../components/ui";
import React from "react";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import Logos from "../components/Logos";

export const metadata = { title: "Customers" };

const AUDIENCES = [
	[
		"Aviation operators",
		"Airlines, business jet operators, charter and cargo operators. Use our data for fleet planning, route economics, contact databases, and operational decisions.",
	],
	[
		"Aircraft manufacturers",
		"OEMs, parts and engine manufacturers, and component suppliers. Use our data for fleet visibility, customer tracking, market analysis, and order forecasting.",
	],
	[
		"Aftermarket and MROs",
		"Maintenance, repair, and overhaul providers, plus the wider aviation aftermarket. Use our data for tracking maintenance events, identifying customers, and planning shop capacity.",
	],
	[
		"Lessors and aviation finance",
		"Aircraft lessors, leasing arrangers, banks, and aviation finance houses. Use our data for portfolio management, lease tracking, asset valuation, and origination.",
	],
	[
		"Insurance and risk",
		"Aviation insurers, reinsurers, and risk pricing teams. Use our data for underwriting, accident and incident analysis, and exposure modeling.",
	],
	[
		"Data and technology companies",
		"Data providers, software vendors, analytics platforms, and consultancies. Build products on our data, often reselling it as part of their own offerings.",
	],
	[
		"Investors and researchers",
		"Private equity, venture capital, hedge funds, analysts, and academic researchers. Use our data for due diligence, market analysis, and research.",
	],
	[
		"Government and regulators",
		"Civil aviation authorities, government agencies, and policy researchers. Use our data for monitoring, statistics, and regulatory work.",
	],
	[
		"Independent builders and startups",
		"Solo developers, small teams, aviation tech startups, and people building tools with AI. Use our data to power new products and applications.",
	],
];

const USES = [
	[
		"Asset management tools",
		"Software that tracks aircraft, owners, lease terms, and renewal dates across a portfolio.",
		"A lessor tracks lease expiry across 800 aircraft and forecasts revenue by type and operator.",
		"Aircraft · Ownership and valuations · Operators",
	],
	[
		"Fleet dashboards",
		"Internal tools showing fleet composition, ages, utilization, and routes.",
		"An OEM tracks the global B787 fleet and its utilization to inform aftermarket strategy.",
		"Aircraft · Operators · Flight activity",
	],
	[
		"Risk and pricing models",
		"Models that price insurance, financing, or aircraft asset risk.",
		"An insurer prices a hull policy using fleet age, operator safety record, and utilization.",
		"Ownership and valuations · Safety · Flight activity",
	],
	[
		"AI assistants and agents",
		"LLM-powered assistants and search tools over the catalog and news archive.",
		"A startup builds an AI assistant answering questions about aircraft, operators, and news.",
		"News · plus any other feed",
	],
	[
		"Market intelligence and research",
		"Reports and models analysing industry dynamics and market trends.",
		"A consultancy analyses cargo capacity growth using fleet, utilization, and operator data.",
		"Any combination of feeds",
	],
	[
		"Products built on ch-aviation data",
		"Commercial products built using our feeds, sometimes resold under another brand.",
		"An analytics company builds a fleet planning product sold to airlines and lessors.",
		"Any combination of feeds",
	],
];

export default function Customers() {
	return (
		<>
			<section className="section-hero tw-bg-white">
				<div className="container">
					<h1 className="h2 tw-my-5 tw-max-w-6xl">
						Who uses the Data Store.
					</h1>
					<p className="lead tw-max-w-2xl">
						From global manufacturers to solo developers, everyone
						working with aviation data uses our data feeds for every
						kind of project, from internal tools to entire products
						of their own.
					</p>
				</div>
			</section>

			<section className="section-clients tw-border-y tw-border-line tw-bg-surface tw-py-11">
				<div className="container">
					<Logos logos={CUSTOMER_LOGOS} />
					<div
						className="tw-mt-4.5 tw-text-center tw-text-[14px] tw-text-muted"
						style={{ marginTop: 18 }}
					>
						Trusted by 800+ clients. Delivering data feeds for 15+
						years.
					</div>
				</div>
			</section>

			<Section>
				<SectionHead title="Customers across the industry." />
				<div className="cards-list tw-grid tw-grid-cols-1 md:tw-grid-cols-3">
					{AUDIENCES.map(([t, d]) => (
						<Card key={t} title={t} body={d} />
					))}
				</div>
			</Section>

			<Section variant="surface">
				<SectionHead title="What customers do with the data." />
				<div className="cards-list tw-grid tw-grid-cols-1 md:tw-grid-cols-3">
					{USES.map(([t, d, e, f]) => (
						<div key={t} className="card">
							<h3 className="tw-mb-1.5 tw-text-base">{t}</h3>
							<p className="tw-text-[13.5px] tw-leading-relaxed">
								{d}
							</p>
							<p className="tw-mt-2 tw-text-[13px] tw-italic tw-text-muted">
								Example: {e}
							</p>
							<div className="tw-mt-3 tw-text-[11.5px] tw-text-muted">
								{f}
							</div>
						</div>
					))}
				</div>
			</Section>

			<Section>
				<SectionHead center title="Data that works together." />
				<div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
					<div className="tw-rounded-panel tw-border tw-border-line tw-bg-surface tw-p-6">
						<h3 className="tw-mb-3.5 tw-text-base">
							Without ch-aviation data
						</h3>
						<p className="tw-text-[14px] tw-leading-relaxed">
							Buy a feed from one vendor, another from a second, a
							third from a third. Each uses different codes for
							the same aircraft, different naming for the same
							operator. Before you can do anything useful, you
							spend weeks mapping IDs, deduplicating records, and
							resolving conflicts. Every time a vendor updates,
							you do it again.
						</p>
					</div>
					<div
						className="tw-rounded-panel tw-border tw-p-6"
						style={{
							background: "#FBEDED",
							borderColor: "#F0D4D5",
						}}
					>
						<h3 className="tw-mb-3.5 tw-text-base">
							With ch-aviation data
						</h3>
						<p className="tw-text-[14px] tw-leading-relaxed">
							Every feed uses ch-aviation&apos;s unique
							identifiers. Aircraft 621 is Aircraft 621 across
							every feed, every vendor, every product. Combine 3
							feeds or 30, they join cleanly on the first key.
							Updates flow in automatically. No mapping, no
							deduplication, no surprises.
						</p>
					</div>
				</div>
			</Section>

			<Testimonials
				testimonials={TESTIMONIALS}
				title="What our customers say"
			/>

			<CtaBand
				title="Ready to join them?"
				body="Tell us what you're building. We'll help you scope the right feeds and get you started."
				button="Talk to us"
			/>
		</>
	);
}
